import type { ManagePageId } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { noAccess } from '../app/access';
import type { RouteHandle } from '../app/RootLayout';
import { requireMe } from '../session/loaders';
import {
  MANAGE_PAGE,
  MANAGE_SPOT_SECTION,
  SPOT_TAB_PAGE,
  appointmentsMoved,
  hasManagePage,
  manageGroupsOf,
  spotWorkHref,
  type ManageSpotSection,
  type ManageSpotTab,
} from './pages';

/** How each Spot work tab shows in Help and Search & jump, as its hero names it. */
const SPOT_WORK: Readonly<Record<ManageSpotTab, { label: string; meta: string }>> = {
  review: { label: 'Spot Committee review', meta: 'Spot nominations waiting for the committee' },
  all: { label: 'All Spot awards', meta: 'Where every Spot nomination is and who it waits on' },
  supervision: {
    label: 'Spot supervisor decisions',
    meta: 'Your people the committee recommended, to approve or veto',
  },
  funding: { label: 'Spot funding requests', meta: 'Spot Awards waiting on Executive funding' },
  // APP-249: the Stock Admin team checks and releases them (APP-244); the committee reads them.
  fulfillment: {
    label: 'Spot letters',
    meta: 'Each Spot Award’s letter, which the Stock Admin team checks and releases',
  },
  statements: {
    label: 'Spot earning statements',
    meta: 'Check what Payroll paid for each Spot Award',
  },
};

/** A page under Manage the person has as a tab: whom Search & jump offers it to. */
const forManagePage =
  (page: ManagePageId) =>
  (me: Parameters<typeof hasManagePage>[0]): boolean =>
    hasManagePage(me, page);

/**
 * Manage (APP-234): its own address opens on the person's first tab, and the Spot work tabs live at
 * `/manage/spot/<section>`. The Stock Admin team's, Payroll's and the approvals pages are the
 * ops and close routes, at their Manage addresses. Every page checks access itself as before:
 * these routes add no reader.
 */
export function manageRoutes(queryClient: QueryClient): RouteObject[] {
  const spot = (Object.entries(MANAGE_SPOT_SECTION) as [ManageSpotSection, ManageSpotTab][]).map(
    ([section, tab]): RouteObject => ({
      path: `manage/spot/${section}`,
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        return null;
      },
      handle: {
        help: { pageLabel: SPOT_WORK[tab].label, topicIds: ['spot', 'waiting'] },
        search: { ...SPOT_WORK[tab], visible: forManagePage(SPOT_TAB_PAGE[tab]) },
      } satisfies RouteHandle,
      lazy: async () => {
        const { SpotPage } = await import('../spot/SpotPage');
        return { element: <SpotPage manageTab={tab} /> };
      },
    }),
  );
  return [
    {
      // No page of its own, so it redirects in a preview too: the first of the person's tabs.
      path: 'manage',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        const first = manageGroupsOf(me)[0]?.pages[0];
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router shows a thrown response as a route error.
        if (first === undefined) throw noAccess('people who do work for others');
        return redirect(MANAGE_PAGE[first].href);
      },
    },
    ...spot,
    {
      // APP-235: the Supervisor appointments tab is gone. Its address (and any bookmark or link to
      // one colleague's appointment) opens Spot awards at "No manager set" with the same query. No
      // page of its own, so it redirects in a preview too.
      path: 'manage/spot/appointments',
      loader: ({ request }: LoaderFunctionArgs) => {
        const url = new URL(request.url);
        return redirect(appointmentsMoved(url.searchParams, url.hash));
      },
    },
  ];
}

/**
 * Recognize › Spot's old work tabs (`/recognize/spot?tab=review`, …) open the same tab under Manage
 * with every other part of the query (`stage`, `award`, `request`, `nomination`, `person`); a
 * nomination, My nominations and Received stay where they are. Null when the address isn't one.
 * Only the list address moved: a nomination's own page (`/recognize/spot/:id`, its supervision
 * page) keeps its address and its whole query, whatever `tab` says (release 45 review).
 */
export function movedSpotTab(url: URL): string | null {
  if (url.pathname !== '/recognize/spot' && url.pathname !== '/recognize/spot/') return null;
  const tab = url.searchParams.get('tab');
  // APP-235: the old appointments tab is Spot awards at "No manager set" now.
  if (tab === 'assignments') return appointmentsMoved(url.searchParams, url.hash);
  if (tab === null || !Object.hasOwn(SPOT_TAB_PAGE, tab)) return null;
  const rest = new URLSearchParams(url.searchParams);
  rest.delete('tab');
  return `${spotWorkHref(tab as ManageSpotTab, rest)}${url.hash}`;
}
